"use client";

import { formatCurrency } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import type { PropertyListing } from "@/lib/ratespot";
import type L from "leaflet";

interface PropertyMapProps {
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  listingUrl: string;
  comps?: PropertyListing[];
  propertyPrice: number;
}

const MAP_CONFIG = {
  zoom: 20,
  minZoom: 8,
  maxZoom: 22,
  tileLayer: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: "© OpenStreetMap contributors",
} as const;

// Custom map style to match our color scheme
const MAP_STYLE = {
  styles: [
    {
      elementType: "geometry",
      stylers: [{ color: "#F5F0EC" }], // chalk color
    },
    {
      elementType: "labels.text.fill",
      stylers: [{ color: "#595959" }], // darkGray color
    },
    {
      elementType: "labels.text.stroke",
      stylers: [{ color: "#FFFFFF" }], // iceWhite color
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#E6C1A9" }], // plaster-light color
    },
  ],
};

export function PropertyMap({
  address,
  city,
  state,
  latitude,
  longitude,
  listingUrl,
  comps,
  propertyPrice,
}: PropertyMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const compMarkersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const initializeMap = async () => {
      const L = (await import("leaflet")).default;

      if (!mapRef.current) {
        // Create map instance
        mapRef.current = L.map("property-map", {
          ...MAP_CONFIG,
          center: [latitude, longitude],
          zoomControl: false,
        });

        // Add tile layer
        L.tileLayer(
          "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
          {
            attribution: MAP_CONFIG.attribution,
          },
        ).addTo(mapRef.current);

        // Add zoom control
        L.control.zoom({ position: "topright" }).addTo(mapRef.current);

        // Main property marker
        const mainIcon = L.divIcon({
          className: "bg-transparent",
          html: `<div class="relative">
            <div class="w-6 h-6 bg-canary rounded-full border-2 border-white shadow-lg flex items-center justify-center">
              <div class="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });

        // Add main property marker
        markerRef.current = L.marker([latitude, longitude], { icon: mainIcon })
          .addTo(mapRef.current)
          .bindPopup(
            `<div class="text-sm">
              <div class="font-medium text-lg">${formatCurrency(propertyPrice)}</div>
              <div class="text-muted-foreground">${address}</div>
              <div class="text-muted-foreground">${city}, ${state}</div>
            </div>`,
            {
              offset: [0, -12],
              className: "property-popup",
            },
          );

        // Set initial zoom level for approximately 2-mile radius
        mapRef.current.setView([latitude, longitude], 14);

        // Add comp markers if they exist
        if (comps?.length) {
          // biome-ignore lint/complexity/noForEach: <explanation>
          comps.forEach((comp) => {
            if (!comp.latitude || !comp.longitude) {
              console.warn(
                "Missing coordinates for comp:",
                comp.address.displayAddress,
              );
              return;
            }

            const price =
              comp.pricing.sold?.amount || comp.pricing.sale?.amount;
            if (!price) {
              // console.warn(
              //   "Missing price for comp:",
              //   comp.address.displayAddress,
              // );
              return;
            }

            const compIcon = L.divIcon({
              className: "bg-transparent",
              html: `<div class="relative">
                <div class="w-6 h-6 bg-plaster-dark rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <div class="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <div class="absolute -top-8 left-1/2 -translate-x-1/2 bg-plaster-dark text-white px-2 py-1 rounded-full shadow-md text-xs whitespace-nowrap">
                  ${formatCurrency(price)}
                </div>
              </div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            });

            const marker = L.marker([comp.latitude, comp.longitude], {
              icon: compIcon,
            })
              .addTo(mapRef.current!)
              .bindPopup(
                `<div class="text-sm">
                  <div class="font-medium">${formatCurrency(price)}</div>
                  <div class="text-muted-foreground">${comp.facets.bedrooms} beds, ${comp.facets.bathrooms} baths</div>
                  <div class="text-muted-foreground">${comp.facets.homeSize?.value} sqft</div>
                </div>`,
                {
                  offset: [0, -10],
                  className: "comp-popup",
                },
              );

            compMarkersRef.current.push(marker);
          });

          // Log bounds calculation
          const validComps = comps.filter(
            (comp) => comp.latitude && comp.longitude,
          );

          if (validComps.length > 0) {
            const bounds = L.latLngBounds([
              [latitude, longitude] as [number, number],
              ...validComps.map(
                (comp) => [comp.latitude!, comp.longitude!] as [number, number],
              ),
            ]);

            // Add padding to bounds to ensure 2-mile radius minimum
            const extendedBounds = bounds.pad(0.3); // 30% padding
            mapRef.current.fitBounds(extendedBounds, {
              padding: [50, 50],
              maxZoom: 14, // Ensure we don't zoom in too far
            });
          }
        } else {
          // If no comps, just center on the main property with 2-mile radius view
          mapRef.current.setView([latitude, longitude], 14);
        }
      }
    };

    initializeMap().catch(console.error);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [address, city, state, latitude, longitude, comps]);

  return (
    <div className="overflow-hidden rounded-xl border bg-white relative z-0">
      <div className="relative aspect-[4/3] md:aspect-[3/1] w-full overflow-hidden">
        <div id="property-map" className="w-full h-full" />
      </div>

      <div className="flex items-center justify-between p-3 text-sm text-muted-foreground">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-canary" />
            <span>Subject Property</span>
          </div>
          {comps && (
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
              <span>Comparable Sales</span>
            </div>
          )}
        </div>
        <Link
          href={listingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          View on Zillow
          <ExternalLink className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
